<template>
  <div class="workbench-table wb-native-host" :class="'wb-native-host--'+kind">
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <div v-if="authorizing" v-loading="true" class="wb-native-loading" :element-loading-text="$LANG('1959', '加载中')" />
    <component v-if="authorized" :is="nativePanel" ref="native" :workbench-height="bodyHeight" :workbench-active="active"
      @loaded="onLoaded" @failed="$emit('failed',$event)" @changed="$emit('changed',$event)" />
    <el-button v-if="error && active" size="small" @click="authorize">{{ $LANG('', '重新加载') }}</el-button>
  </div>
</template>
<script>
// Keep the compiled SFC (including scope id, metadata renderers, permissions and
// business handlers) as the source of truth. Only host lifecycle/layout bridges
// belong here; never clone the business implementation into the workbench.
function createNativePage(component, kind) {
  const source = component.options || component
  const page = { ...source, name: `SupplierNative_${kind}` }
  delete page._Ctor
  page.props = {
    ...(source.props || {}),
    workbenchActive: { type: Boolean, default: true },
    workbenchHeight: { type: Number, default: 500 }
  }
  const overlays = ['drawer','cfmWin','nextWin','repWin','repDlg','addWin','dtlWin','poWin','matWin','saleWin','empWin','promptWin','prodWin','loadWin','videoFlag','setWin','editWin','showViewer']
  const methods = source.methods || {}
  page.methods = {
    ...methods,
    initPage(...args) {
      const result = methods.initPage && methods.initPage.apply(this, args)
      this.$emit('loaded', { kind, rows: this.allData || [] })
      return result
    },
    workbenchCloseOverlays() {
      overlays.forEach(key => { if (typeof this[key] === 'boolean') this[key] = false })
    },
    workbenchRefresh() {
      return methods.qryTabData.call(this)
    }
  }
  page.computed = { ...source.computed, ...(source.workbenchReconcile ? {} : { tabHeight: () => '100%' }) }
  page.watch = {
    ...(source.watch || {}),
    workbenchActive(value) { if (!value) this.workbenchCloseOverlays() }
  }
  // Dialog closure includes cancel: a harmless read refresh reconciles native
  // in-place confirmation and generation, without duplicating the write APIs.
  const dialogs = kind === 'orders' ? ['cfmWin', 'nextWin'] : (kind === 'deliveries' || source.workbenchReconcile) ? ['addWin','editWin'] : []
  overlays.forEach(key => {
    const previous = page.watch[key]
    page.watch[key] = function (value, old) {
      if (value && !this.workbenchActive) { this[key] = false; return }
      if (typeof previous === 'function') previous.call(this, value, old)
      if (this.workbenchActive && old && !value && dialogs.includes(key)) this.$emit('changed', { kind })
      if (this.workbenchActive && value && key === 'promptWin') this.$emit('failed', kind)
    }
  })

  return page
}


const nativePanels = {
  orders: () => import('./OrderSection.vue'),
  deliveries: () => import('./DeliverySection.vue'),
  ledger: () => import('./ReconcileSection.vue')
}
export default {
  name: 'SupplierWorkbenchTable',
  props: {
    kind: { type: String, required: true, validator: value => !!nativePanels[value] },
    api: { type: Object, required: true }, context: { type: Object, required: true },
    active: { type: Boolean, default: true },
    bodyHeight: { type: Number, default: 420 }
  },
  data() { return { authorized: false, authorizing: false, error: '', epoch: 0, nativePanel: null } },
  computed: { loading() { const page = this.$refs.native; return this.authorizing || !!(page && (page.loading || page.v_loading)) } },
  watch: { active(value) { if (!value) { this.epoch++; this.authorizing = false } else if (!this.authorized) this.authorize() } },
  mounted() { if (this.active) this.authorize() },
  beforeDestroy() { this.epoch++ },
  methods: {
    async authorize() {
      if (this.authorizing) return
      const epoch = ++this.epoch
      this.authorizing = true; this.error = ''
      try {
        if (!this.context.userNo || !this.context.corpId) throw new Error(this.$LANG('', '会话信息不完整，请重新登录'))
        await this.api.permission(this.kind)
        if (epoch !== this.epoch) return
        const loaded = await nativePanels[this.kind]()
        if (epoch !== this.epoch) return
        this.nativePanel = createNativePage(loaded.default || loaded, this.kind)
        this.authorized = true
      } catch (error) {
        if (epoch === this.epoch) { this.error = error.message || this.$LANG('', '查询失败'); this.$emit('failed', this.kind) }
      } finally { if (epoch === this.epoch) this.authorizing = false }
    },
    refresh() {
      if (!this.active) return Promise.resolve(false)
      if (!this.authorized) return this.authorize().then(() => this.authorized)
      const page = this.$refs.native
      if (!page) return Promise.resolve(false)
      // Native query methods manage their own loading, paging and error UI.
      page.workbenchRefresh()
      return Promise.resolve(true)
    },
    onLoaded(payload) { this.$emit('loaded', payload) }
  }
}
</script>
<style scoped>
.wb-native-host { display: flex; flex-direction: column; flex: 1 1 0; height: 100%; min-height: 0; min-width: 0; overflow: hidden; background: #fff; font: 12px/1.5 "Helvetica Neue",Helvetica,"PingFang SC","Hiragino Sans GB","Microsoft YaHei","微软雅黑",Arial,sans-serif; color: #515a6e; }
.wb-native-loading { flex: 1; min-height: 100px; }
/* Host layout only. Native table ids, scope ids, headers, controls and row
   renderers retain exactly the styles shipped by their original Vue pages. */
.wb-native-host ::v-deep .ve-list-page { display: flex; flex-direction: column; height: 100%; flex: 1 1 0; min-height: 0; min-width: 0; overflow: hidden; }
.wb-native-host ::v-deep .ve-list-page__body { display: flex; flex-direction: column; flex: 1 1 0; min-height: 0; min-width: 0; overflow: hidden; }
.wb-native-host ::v-deep .ve-list-page__table { flex: 1 1 0; min-height: 0; min-width: 0; overflow: hidden; }
.wb-native-host ::v-deep .ve-list-page__table>.vue-table-root,
.wb-native-host ::v-deep .ve-list-page__body>.vue-table-root,
.wb-native-host ::v-deep .ve-table,
.wb-native-host ::v-deep .ve-table-container { height: 100% !important; max-height: 100% !important; }
.wb-native-host ::v-deep .ve-list-page__pagination { display: flex; flex: 0 0 38px; min-height: 38px; overflow: hidden; align-items: flex-start; padding-top: 3px; box-sizing: border-box; background: #fff; font-size: 12px; }
.wb-native-host--deliveries ::v-deep .ve-list-page>div:first-child { display: none !important; }
/* Preserve every original toolbar control, allow reaching them at narrow widths. */
.wb-native-host--orders ::v-deep .suppo-ord-toolbar { min-width: 0; overflow-x: auto; flex-shrink: 0; }
.wb-native-host--orders ::v-deep .suppo-ord-toolbar>* { flex-shrink: 0; }
.wb-native-host--deliveries ::v-deep .suppo-dely-toolbar { min-width: 0; overflow-x: auto; flex-shrink: 0; }
.wb-native-host--deliveries ::v-deep .suppo-dely-toolbar>* { flex-shrink: 0; }
.wb-native-host--deliveries ::v-deep .ve-list-page>div[style*="min-width: 1300px"],
.wb-native-host--deliveries ::v-deep .ve-list-page>div[style*="min-width:1300px"] { min-width: 0 !important; flex-shrink: 0; overflow-x: auto; }
.wb-native-host--deliveries ::v-deep .ve-list-page>div[style*="min-width"]>* { flex-shrink: 0; }
.wb-native-host--ledger ::v-deep .ve-list-page>div:first-child { flex-shrink: 0; overflow-x: auto; }

/* 增强表格顶部与表头边框对比度，使其清晰明显 */
.wb-native-host ::v-deep .ve-table {
    border-top: 1px solid #94a3b8 !important;
}
.wb-native-host ::v-deep .ve-table-header-tr {
    border-top: 1px solid #94a3b8 !important;
}
.wb-native-host ::v-deep .ve-table-header-th {
    border-top: 1px solid #94a3b8 !important;
    border-bottom: 2px solid #64748b !important;
    border-right: 1px solid #cbd5e1 !important;
    border-left: 1px solid #cbd5e1 !important;
}
.wb-native-host ::v-deep .table-head-cell-class {
    border-top: 1px solid #94a3b8 !important;
    border-bottom: 2px solid #64748b !important;
    border-right: 1px solid #cbd5e1 !important;
    border-left: 1px solid #cbd5e1 !important;
}
</style>
